const handleError = (error, method) => {
  console.error(`Error in ${method} request:`, error);
  throw error;
};

const apiService = {
  get: async (url, data = null) => {
    try {
      // Filter out null, undefined, or empty string values from data
      const filteredData = Object.fromEntries(
        Object.entries(data || {}).filter(
          ([key, value]) => value != null && value !== ""
        )
      );

      const token = localStorage.getItem("token");

      // Convert filtered data to query string
      const queryParams = Object.keys(filteredData).length
        ? `?${new URLSearchParams(filteredData).toString()}`
        : "";

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${url}${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "", // Add Bearer token
          },
        }
      );
      if (response.status === 204) {
        return null; // No content to return
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message);
      }
      return result;
    } catch (error) {
      handleError(error.message, "GET");
    }
  },

  post: async (url, data, contentType = "json") => {
    try {
      const token = localStorage.getItem("token");

      // Prepare headers
      const headers = {
        Authorization: token ? `Bearer ${token}` : "",
      };

      let body;

      if (contentType === "multipart") {
        // For multipart/form-data, let the browser set the Content-Type header
        // and handle the boundary automatically
        body = data; // Assuming `data` is already a FormData object
      } else {
        // Default to JSON
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(data);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${url}`,
        {
          method: "POST",
          headers,
          body,
        }
      );
      const result = await response.json();
      if (!response.ok) {
        if (result.errors && Array.isArray(result.errors)) {
          console.error("POST detailed errors:", result.errors);
          throw new Error(
            result.errors.map((err) => `${err.path}: ${err.msg}`).join(", ")
          );
        }

        throw new Error(result.message || "An unknown error occurred");
      }

      return result;
    } catch (error) {
      console.log("Error in POST request:", error);
      handleError(error.message, "POST");
    }
  },

  put: async (url, data) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${url}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message);
      }
      return result;
    } catch (error) {
      handleError(error.message, "PUT");
    }
  },

  delete: async (url) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${url}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      if (response.status === 204) {
        return null; // No content to return
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message);
      }
      return result;
    } catch (error) {
      handleError(error.message, "DELETE");
    }
  },
  download: async (url) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${url}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "", // Add Bearer token
          },
        }
      );
      const result = await response.blob();
      if (!response.ok) {
        throw new Error(result.message);
      }
      return result;
    } catch (error) {
      handleError(error.message, "GET");
    }
  },
};

export default apiService;
